import React from 'react';
import './CheckoutPage.sass';

import * as Cookie from 'js-cookie';
import { Helmet } from 'react-helmet';
import { browserHistory } from 'react-router';
import { injectIntl, FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import {
  applyCouponToCart,
  checkoutCart,
  removeCouponFromCart,
  removeProductFromCart,
  applyWalletPaymentToCart,
} from '../../ducks/cart';
import {openModal} from '../../ducks/ui';
import { getOrderPaymentData } from '../../ducks/order';
import { setBackground } from '../../ducks/ui';
import { analyticsClearEcommerce, analyticsViewCart } from '../../middlewares/analytics/actions';
import asSubmissionError, { asMappedSubmissionError } from '../../utils/asSubmissionError';
import { getGaClientId, getGaSessionId } from '../../utils/extractGaClientId';
import { getFirstElement } from '../../utils/getFirstElement';
import getPublicUrl from '../../utils/getPublicUrl';

import Breadcrumbs from '../../components/breadcrumbs/Breadcrumbs';
import CartProduct from '../../components/cartProduct/CartProduct';
import Button from '../../components/button/Button';
import CheckoutCouponForm from '../../components/checkout/CheckoutCouponForm';
import CheckoutForm from '../../components/checkout/CheckoutForm';
import CheckoutSteps from '../../components/checkout/CheckoutSteps';
import CheckoutWallet from '../../components/checkout/CheckoutWallet';
import ChannelPrice from '../../containers/channelUtils/ChannelPrice';
import Pricing from '../../components/pricing/Pricing';
import Tooltip from '../../components/tooltip/Tooltip';
import AuthFlowModal from '../auth/modals/AuthFlowModal';
import OrderPage from '../orderPage/OrderPage';
import ProductVariantContainer from '../../components/productVariants/ProductVariantContainer';
import withChannelFormatters from '../channelUtils/withChannelFormatters';

@injectIntl
@withChannelFormatters
@connect(({ cart, user }) => ({
  cart,
  profile: user.profile,
}))
export default class CheckoutPage extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {checkoutMethod: {}};
  }

  checkout = ({ checkoutMethod, ...checkoutFields }) => {
    const { dispatch, cart, profile, channelFormatters } = this.props;

    if (profile) {
      const { customer, ...allowedFields } = checkoutFields;
      checkoutFields = allowedFields;
    }

    return dispatch(
      checkoutCart(
        cart.id,
        cart.walletPaymentAmount === cart.itemsTotal? null : checkoutMethod,
        checkoutFields,
        cart.walletPaymentAmount,
        Cookie.get('affiliate'),
        cart.tokenValue,
        getGaClientId(),
        getGaSessionId()
      )
    )
      .catch(
        asMappedSubmissionError({
          'payments.0.method': 'checkoutMethod',
        })
      )
      .then((orderResponse) => {
        const order = orderResponse.content;

        if (order.payments.length) {
          const payment = getFirstElement(order.payments);
          const paymentMethod =
            payment.method &&
            payment.methodsAvailable.find((it) => it.code === payment.method.code);

          if (paymentMethod && paymentMethod.redirect) {
            return paymentMethod.redirect;
          }
        }

        const returnUrl =
          getPublicUrl() +
          channelFormatters.link(
            OrderPage.getOrderReturnPath({
              ...orderResponse.content,
              anonymous: cart.anonymous,
            })
          );

        return dispatch(
          getOrderPaymentData(
            orderResponse.content.id,
            orderResponse.content.tokenValue,
            returnUrl,
            getGaClientId(),
            getGaSessionId()
          )
        )
          .then((paymentDataResponse) => paymentDataResponse.content.url)
          .catch(() => returnUrl);
      })
      .then((redirectLocation) => {
        window.location = redirectLocation;

        // Keep form locked until page transition
        return new Promise(() => {});
      });
  };

  componentDidMount() {
    const { dispatch, cart } = this.props;

    dispatch(setBackground());

    if (cart) {
      dispatch(analyticsClearEcommerce());
      dispatch(analyticsViewCart(cart));
    }
  }

  applyWallet = useWalletFunds => {
    const {dispatch, cart} = this.props;

    const walletFunds = useWalletFunds >= cart.itemsTotal ? cart.itemsTotal : useWalletFunds;

    return dispatch(applyWalletPaymentToCart(cart.id, walletFunds, cart.tokenValue));
  };

  render() {
    const { intl, dispatch, cart, profile, channelFormatters } = this.props;

    if (!cart || !cart.items.length) {
      return (
        <div>
          <Helmet>
            <title>{intl.formatMessage({ id: 'page.checkout.title' })}</title>
          </Helmet>

          <div className="container">
            <div className="row">
              <div className="page-checkout">
                <Breadcrumbs
                  links={[{ name: intl.formatMessage({ id: 'section.breadcrumbs.cart' }) }]}
                />

                <h1>
                  <FormattedMessage id="page.order.title" />
                </h1>

                <div className="modal__notice">
                  <FormattedMessage id="page.order.empty" />
                </div>

                <div className="modal__notice">
                  <Button
                    filled
                    wide
                    onClick={() => browserHistory.replace(channelFormatters.link('/'))}
                  >
                    <FormattedMessage id="section.generic.back" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const { walletPaymentAmount, itemsTotal } = cart;

    const _total = walletPaymentAmount
      ? walletPaymentAmount >= itemsTotal
        ? 0
        : itemsTotal + (this.state.checkoutMethod.charge || 0) - walletPaymentAmount
      : itemsTotal + (this.state.checkoutMethod.charge || 0);
    const _subtotal = walletPaymentAmount
      ? walletPaymentAmount >= itemsTotal
        ? itemsTotal
        : itemsTotal +
          (this.state.checkoutMethod.originalCharge || this.state.checkoutMethod.charge || 0)
      : undefined;

    return (
      <div>
        <Helmet>
          <title>{intl.formatMessage({ id: 'page.checkout.title' })}</title>
        </Helmet>

        <div className="container">
          <div className="row">
            <div className="page-checkout">
              <Breadcrumbs links={[{ name: intl.formatMessage({ id: 'page.checkout.title' }) }]} />

              <h1>
                <FormattedMessage id="page.order.title" />
              </h1>

              <div className="checkout">
                <CheckoutSteps active={CheckoutSteps.Steps.INITIAL} />

                <div className="checkout__form">
                  <CheckoutForm
                    onSubmit={this.checkout}
                    checkoutMethods={getFirstElement(cart.payments).methodsAvailable}
                    userProfile={profile}
                    cartItems={cart.items}
                    cartValue={_total}
                    cartOriginalValue={_subtotal}
                    onSetCheckoutMethod={(method) => this.setState({checkoutMethod: method})}
                  >
                    <CheckoutCouponForm
                      coupon={cart.promotionCoupon && cart.promotionCoupon.code}
                      walletFundsApplied={cart.walletPaymentAmount}
                      applyCoupon={(coupon) =>
                        dispatch(applyCouponToCart(coupon, cart.id, cart.tokenValue)).catch(
                          asSubmissionError
                        )
                      }
                      removeCoupon={() =>
                        dispatch(removeCouponFromCart(cart.id, cart.tokenValue)).catch(
                          asSubmissionError
                        )
                      }
                      initialValues={{
                        promotionCoupon:
                          cart.promotionCoupon && cart.promotionCoupon.code
                            ? cart.promotionCoupon.code
                            : '',
                      }}
                    />

                    {profile ? (
                      <CheckoutWallet
                        selected={
                          cart.walletPaymentAmount >= cart.itemsTotal ? cart.itemsTotal : cart.walletPaymentAmount
                        }
                        max={cart.total}
                        available={profile.wallet.balance}
                        couponActived={!!(cart.promotionCoupon && cart.promotionCoupon.code)}
                        onChange={this.applyWallet}
                      />
                    ) : (
                      <div className="checkout__sign-btns">
                        <label>
                          <span className='checkout__sign-btns-btn' onClick={() => dispatch(openModal(AuthFlowModal))}>
                            <FormattedMessage id="section.auth.sign-in.action"/>
                          </span>
                          {' '}
                          <FormattedMessage id="page.checkout.cashback.or"/>
                          {' '}
                          <span className='checkout__sign-btns-btn' onClick={() => dispatch(openModal(AuthFlowModal, {initialState: 'signUp'}))}>
                            <FormattedMessage id="section.auth.sign-up.action"/>
                          </span>
                          {' '}
                          <FormattedMessage id="page.checkout.cashback.receive"  values={{amount: <ChannelPrice component="b" price={cart.cashback.amount}/>}} />
                        </label>
                      </div>
                    )}

                    <div className="checkout__total">
                      <Pricing
                        accented
                        between
                        annotation={`${intl.formatMessage({ id: 'page.checkout.total' })}:`}
                        value={_total}
                        originalValue={_subtotal}
                      />

                      {this.state.checkoutMethod.charge && _total > 0 && (
                        <div>
                          <FormattedMessage
                            id='page.checkout.charge'
                            values={{ amount: <ChannelPrice component='b' price={this.state.checkoutMethod.charge} /> }}
                          />
                        </div>
                      )}

                      <div className='checkout__total-cashback'>
                        <FormattedMessage
                          id='page.checkout.cashback.total'
                          values={{ amount: <ChannelPrice component='b' price={cart.cashback.amount} /> }}
                        />

                        <Tooltip
                          content={<FormattedMessage id="page.checkout.cashback.tooltip.short" />}
                          open={this.state.openTooltip}
                        >
                          <div className='tooltip_trigger' onMouseLeave={() => this.setState({openTooltip: false})} onMouseEnter={() => this.setState({openTooltip: true})} />
                        </Tooltip>
                      </div>
                    </div>

                    <div className="checkout__products">
                      {cart.items.map((item) => (
                        <div key={item.id} className="checkout__product">
                          <CartProduct
                            eventOccurrence={item.event && item.eventOccurrence}
                            image={item.variant.product.images[0]}
                            name={CartProduct.getItemTitle(item, cart.createdFromAdmin)}
                            options={ProductVariantContainer.describeProductChoice(item)}
                            price={item.total}
                            onRemoveClick={() => dispatch(removeProductFromCart(item.id))}
                          />
                        </div>
                      ))}
                    </div>
                  </CheckoutForm>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./containers/checkoutPage/CheckoutPage.js